import { API_PREFIX_POST_PATH, API_PREFIX_TAG_PATH, API_PREFIX_WEBSITE_PATH } from "@/constants/prefix-path-api"
import { message } from "@/lib/config/lib/antd-global"
import { generate6RandomChars } from "@/utils/generate-6-random-chars"
import { generateSlug } from "@/utils/remove-accents"
import { AxiosRequestConfig } from "axios"
import { HttpClient } from "../../../../app/api/HttpClient"
import { FilterSearchParams } from "../../../../lib/external-components"
import { AttributesCategory } from "../../ui-builder/config/types"

export interface FetchPostsQueryConfig {
  fields?: string[]
  populate?: string | Record<string, any>
}

export interface SlugAvailabilityResponse {
  isAvailable: boolean
}

interface FetchPostAllQueryConfig {
  fields?: string[]
  populate?: string | Record<string, any> | null
  pageSize?: string
  sort?: string
  publicationState?: "live" | "preview"
}

type PostAllQueryParams = Omit<FilterSearchParams, "populate"> & {
  fields?: string[]
  populate?: string | Record<string, any>
}

/** Field mà ô tìm kiếm của Post list lọc theo — chọn qua dropdown "Tìm theo". */
export const POST_SEARCH_FIELDS = ["title", "slug", "description"] as const
export type PostSearchField = (typeof POST_SEARCH_FIELDS)[number]

const toPostSearchField = (value?: string): PostSearchField =>
  POST_SEARCH_FIELDS.includes(value as PostSearchField) ? (value as PostSearchField) : "title"

/**
 * Điều kiện tìm kiếm của Post list theo đúng 1 field (mặc định tiêu đề).
 * Với slug, dán cả URL bài viết (vd. https://tamdamedia.eu/vi/tin-tuc/abc-xyz)
 * cũng được — lấy segment cuối của đường dẫn.
 *
 * Trả về mảng để gán vào `$and` chứ không gán thẳng vào `filters`, vì tab trạng
 * thái (published/draft) dùng `$or` — tách riêng để hai điều kiện không đè nhau.
 */
const buildPostSearchFilter = (raw: string, field: PostSearchField) => {
  const value = raw.trim()
  if (field === "slug") {
    const slugTerm =
      value
        .replace(/^https?:\/\/[^/]+/i, "")
        .split(/[?#]/)[0]
        .split("/")
        .filter(Boolean)
        .pop() || value
    return [{ slug: { $containsi: slugTerm } }]
  }
  return [{ [field]: { $containsi: value } }]
}

const getBrowserLocale = () => {
  if (typeof window === "undefined") {
    return undefined
  }

  return localStorage.getItem("language") || undefined
}

const resolveLocale = (locale?: string) => locale || getBrowserLocale()

export const fetchPostAll = async (filters = {}, locale?: string, queryConfig: FetchPostAllQueryConfig = {}) => {
  const params: PostAllQueryParams = {
    pagination: {
      pageSize: queryConfig.pageSize || "100",
    },
    populate: "*",
    filters: {},
    sort: queryConfig.sort || "createdAt:desc",
    publicationState: queryConfig.publicationState || "live",
  }

  if (locale) {
    params.locale = locale
  }

  if (filters && typeof filters === "object") {
    params.filters = {
      ...params.filters,
      ...(filters as Record<string, any>),
    }
  }

  if (queryConfig.fields?.length) {
    params.fields = queryConfig.fields
  }

  if (queryConfig.populate !== undefined) {
    if (queryConfig.populate === null) {
      delete params.populate
    } else {
      params.populate = queryConfig.populate
    }
  }

  return await HttpClient.get(`${API_PREFIX_POST_PATH}`, { params })
}

export const fetchPostByCategories = async (categoriesId: string[], locale?: string) => {
  const localeParam = resolveLocale(locale)
  const params: FilterSearchParams = {
    populate: "*",
    sort: "createdAt:desc",
    publicationState: "live",
    filters: {
      categories: {
        // @ts-ignore
        id: {
          $in: categoriesId,
        },
      },
    },
    pagination: {
      pageSize: "100",
    },
  }
  if (localeParam) {
    params.locale = localeParam
  }

  try {
    const response = await HttpClient.get(`${API_PREFIX_POST_PATH}`, { params })

    const modifiedResponse = response.data.map((post: any) => ({
      ...post,
      status: post.attributes.publishedAt ? "published" : "draft",
      reviewStatus: post.attributes?.reviewStatus,
      publishedAt: post.attributes.publishedAt,
    }))

    return {
      data: modifiedResponse,
      meta: response.meta,
    }
  } catch (error: any) {
    message.error("Không thể lấy bài viết. Vui lòng thử lại.")
  }
}

export const fetchPosts = async (
  searchTerms: { [key: string]: string } = {},
  signal?: AbortSignal,
  queryConfig: FetchPostsQueryConfig = {},
) => {
  const params: FilterSearchParams = {
    sort: "createdAt:desc",
    publicationState: "preview",
    filters: {},
    pagination: {
      pageSize: "20",
    },
    locale: "all",
  }

  if (queryConfig.fields && queryConfig.fields.length > 0) {
    ; (params as any).fields = queryConfig.fields
  }

  if (queryConfig.populate !== undefined) {
    ; (params as any).populate = queryConfig.populate
  }

  // `searchField` chỉ chọn field cho ô tìm kiếm (`title`), không phải filter riêng.
  const excludedKeys = ["current", "pageSize", "start", "limit", "total", "page", "searchField"]
  const searchField = toPostSearchField(searchTerms.searchField)

  Object.keys(searchTerms).forEach(key => {
    const value = searchTerms[key]

    if (key === "publicationState") {
      const convertParamStatusPost = value === "draft" || value === "all" ? "preview" : "live"
      params.publicationState = convertParamStatusPost

      if (value === "draft") {
        params.filters.publishedAt = { $null: true }
      } else {
        delete params.filters.publishedAt
      }
    } else if (key === "locale") {
      params.locale = value
    } else if (!excludedKeys.includes(key)) {
      if (key === "categories") {
        params.filters.categories = {
          id: { $eq: value },
        }
      } else if (key === "author") {
        params.filters.author = {
          username: { $containsi: value }
        }
      } else if (key === "domain") {
        params.filters.websites = {
          domain: { $containsi: value },
        }
      } else if (key === "status") {
        if (value === "published") {
          params.publicationState = "live"
          params.filters.$or = [
            { status: { $containsi: "published" } },
            { publishedAt: { $notNull: true } },
          ]
        } else if (value === "draft") {
          params.publicationState = "preview"
          params.filters.$or = [
            { status: { $containsi: "draft" } },
            { publishedAt: { $null: true } },
          ]
        } else if (value && value !== "all") {
          params.filters.status = { $containsi: value }
        }
      } else if (key === "title") {
        if (value) {
          ; (params.filters as any).$and = buildPostSearchFilter(value, searchField)
        }
      } else if (key === "sort") {
        if (value) params.sort = value
      } else {
        if (value) {
          params.filters[key] = { $containsi: value }
        }
      }
    } else if (value) {
      params.pagination = {
        ...params.pagination,
        [key]: value,
      }
    }
  })

  try {
    const response = await HttpClient.get(`${API_PREFIX_POST_PATH}`, { params, signal })
    if (!response) {
      if (signal?.aborted) return
      message.error("Khong the lay bai viet. Vui long thu lai.")
      return
    }
    const modifiedResponse = response.data.map((post: any) => ({
      ...post,
      status: post.attributes.publishedAt ? "published" : "draft",
      reviewStatus: post.attributes?.reviewStatus,
      publishedAt: post.attributes.publishedAt,
    }))

    return {
      data: modifiedResponse,
      meta: response.meta,
    }
  } catch (error: any) {
    const isAbortError =
      signal?.aborted ||
      error?.name === "AbortError" ||
      error?.name === "CanceledError" ||
      error?.code === "ERR_CANCELED"

    if (isAbortError) return

    message.error("Khong the lay bai viet. Vui long thu lai.")
  }
}

export const fetchPostId = async (id: number, locale?: string) => {
  const params: Record<string, string> = {
    populate: "*",
  }
  if (locale) {
    params.locale = locale
  }
  const response = await HttpClient.get(`${API_PREFIX_POST_PATH}/${id}`, { params })

  const postData = response.data
  if (postData && postData.attributes) {
    if (postData.attributes.isLiveNews === undefined) {
      postData.attributes.isLiveNews = false
    }
    if (postData.attributes.liveNewsItem === undefined) {
      postData.attributes.liveNewsItem = []
    }
    if (postData.attributes.views === undefined) {
      postData.attributes.views = 0
    }
  }

  return postData
}

export const deletePost = async (id: number) => {
  return await HttpClient.delete(`${API_PREFIX_POST_PATH}/${id}`)
}

// Lấy slug của các post ĐÃ PUBLISH trong danh sách id, trong MỘT query (tránh
// N+1). Chỉ published vì bản nháp chưa từng public → không có cache để purge.
// `locale: all` vì slug là per-locale và mỗi bản dịch là một entry id riêng.
export const fetchPublishedSlugsByIds = async (ids: number[]): Promise<string[]> => {
  if (ids.length === 0) return []
  const params: AxiosRequestConfig["params"] = {
    fields: ["slug"],
    filters: { id: { $in: ids } },
    publicationState: "live",
    locale: "all",
    pagination: { pageSize: ids.length },
  }
  try {
    const response = await HttpClient.get(`${API_PREFIX_POST_PATH}`, { params })
    return (response?.data ?? [])
      .map((p: { attributes?: { slug?: string } }) => p?.attributes?.slug ?? "")
      .filter(Boolean)
  } catch {
    return []
  }
}
export const updatePost = async (id: number, data: any) => {
  return await HttpClient.put(`${API_PREFIX_POST_PATH}/${id}`, data)
}
export const addPost = async (data: any) => {
  return await HttpClient.post(`${API_PREFIX_POST_PATH}`, data)
}

export const fetchCategories = async (locale?: string, domain?: string) => {
  const localeParam = resolveLocale(locale)
  const params: Record<string, any> = {
    pagination: {
      pageSize: "100",
    },
    sort: "createdAt:desc",
  }
  if (localeParam) {
    params.locale = localeParam
  }
  if (domain) {
    params.filters = { domain: { $eq: domain } }
  }
  const response = await HttpClient.get(`/api/categories`, { params })
  return response
}

export const fetchWebsites = async () => {
  const params = {
    pagination: {
      pageSize: "100",
    },
    sort: "createdAt:desc",
  }

  return await HttpClient.get(`${API_PREFIX_WEBSITE_PATH}`, { params })
}

export const fetchTags = async (locale?: string) => {
  const localeParam = resolveLocale(locale)
  const params: Record<string, any> = {}
  if (localeParam) {
    params.locale = localeParam
  }
  return await HttpClient.get(`/api/tags`, { params })
}

export const createTag = async (data: AttributesCategory) => {
  return await HttpClient.post(`${API_PREFIX_TAG_PATH}`, { data })
}

export const checkSlug = async (slug: string, locale?: string) => {
  const localeParam = resolveLocale(locale)
  const params: AxiosRequestConfig["params"] = {
    filters: {
      slug: { $eq: slug },
    },
    publicationState: "preview",
  }
  if (localeParam) {
    params.locale = localeParam
  }
  return await HttpClient.get(`${API_PREFIX_POST_PATH}`, { params })
}

export const fetchPostByLocale = async (locale?: string) => {
  const params: FilterSearchParams = {
    populate: "*",
    sort: "createdAt:desc",
    publicationState: "live",
    filters: {},
    locale: locale,
  }

  try {
    const response = await HttpClient.get(`${API_PREFIX_POST_PATH}`, { params })
    return response
  } catch (error) { }
}

export const fetchNewestPostsByLocale = async (locale?: string) => {
  const params: FilterSearchParams = {
    populate: "*",
    sort: "createdAt:desc",
    publicationState: "live",
    locale: locale,
    filters: {},
    pagination: {
      pageSize: "1",
    },
  }
  try {
    const response = await HttpClient.get(`${API_PREFIX_POST_PATH}`, { params })
    return response
  } catch (error) {
    return null
  }
}

export const createPostLocalization = async (id: number, data: any) => {
  if (!id) {
    return null
  }

  try {
    const response = await HttpClient.post(`${API_PREFIX_POST_PATH}/${id}/localizations`, data)
    return response
  } catch (error: any) {
    const strapiError = error.response?.data?.error
    const isSlugConflict = strapiError?.details?.errors?.some(
      (e: any) => Array.isArray(e.path) && e.path.includes("slug"),
    )
    if (isSlugConflict) {
      message.error("Đường dẫn đã tồn tại. Vui lòng chọn đường dẫn khác.")
    } else {
      const errorMessage =
        strapiError?.message ||
        error.response?.data?.message ||
        "Không thể tạo bản dịch. Vui lòng thử lại"
      message.error(errorMessage)
    }
    return null
  }
}

export const approvePost = async (id: number) => {
  return await HttpClient.post(`${API_PREFIX_POST_PATH}/${id}/approve`)
}

export const requestReviewPost = async (id: number) => {
  return await HttpClient.post(`${API_PREFIX_POST_PATH}/${id}/submit`)
}

// unpublishAt: bỏ qua = giữ lịch gỡ đang có, null = bỏ lịch gỡ, iso = đặt lịch gỡ.
export const publishPost = async (id: number, unpublishAt?: string | null) => {
  const body = unpublishAt === undefined ? undefined : { unpublishAt }
  return await HttpClient.post(`${API_PREFIX_POST_PATH}/${id}/publish`, body)
}

export const rejectPost = async (id: number, note: string = "No reason provided") => {
  return await HttpClient.post(`${API_PREFIX_POST_PATH}/${id}/reject`, { note })
}
 
export const unPublishPost = async (id: number) => {
  return await HttpClient.post(`${API_PREFIX_POST_PATH}/${id}/unpublish`)
}

// Đặt cả khung hiển thị trong một request. unpublishAt: bỏ qua = giữ, null = bỏ lịch gỡ.
export const schedulePost = async (id: number, scheduledAt: string, unpublishAt?: string | null) => {
  const body = unpublishAt === undefined ? { scheduledAt } : { scheduledAt, unpublishAt }
  return await HttpClient.post(`${API_PREFIX_POST_PATH}/${id}/schedule`, body)
}

// Bài đang live: chỉ chỉnh mốc gỡ qua cùng endpoint schedule. null = bỏ lịch gỡ.
export const schedulePostUnpublish = async (id: number, unpublishAt: string | null) => {
  return await HttpClient.post(`${API_PREFIX_POST_PATH}/${id}/schedule`, { unpublishAt })
}

/**
 * GET /posts/check-slug - Check slug availability.
 * Pass excludeId (current post id) so the backend can skip the record being edited.
 */
export const checkSlugAvailability = async (
  slug: string,
  excludeId?: number,
): Promise<SlugAvailabilityResponse | null> => {
  const params: AxiosRequestConfig["params"] = {
    slug,
    publicationState: "preview",
  }

  if (excludeId) {
    params.excludeId = excludeId
  }

  return await HttpClient.get(`${API_PREFIX_POST_PATH}/check-slug`, { params })
}

export const generateUniqueSlug = async (rawSlug: string, excludeId?: number): Promise<string> => {
  const baseSlug = generateSlug(rawSlug)
  if (!baseSlug) return ""

  let candidate = baseSlug
  const MAX_ATTEMPTS = 10

  for (let i = 0; i < MAX_ATTEMPTS; i++) {
    const response = await checkSlugAvailability(candidate, excludeId)
    if (response?.isAvailable !== false) {
      return candidate
    }
    candidate = `${baseSlug}-${generate6RandomChars()}`
  }

  return candidate
}

/**
 * GET /posts/mine - Get current user's posts
 */
export const fetchPostsMine = async (
  searchTerms: { [key: string]: string } = {},
  signal?: AbortSignal,
  queryConfig: FetchPostsQueryConfig = {}
) => {
  const params: FilterSearchParams = {
    sort: "createdAt:desc",
    publicationState: "preview",
    pagination: {
      pageSize: "20",
    },
    locale: "all",
    filters: {},
  }

  // `searchField` chỉ chọn field cho ô tìm kiếm (`title`), không phải filter riêng.
  const excludedKeys = ["current", "pageSize", "start", "limit", "total", "page", "searchField"]
  const searchField = toPostSearchField(searchTerms.searchField)

  Object.keys(searchTerms).forEach((key) => {
    const value = searchTerms[key]

    if (key === "publicationState") {
      const convertParamStatusPost = value === "draft" || value === "all" ? "preview" : "live"
      params.publicationState = convertParamStatusPost

      if (value === "draft") {
        params.filters.publishedAt = { $null: true }
      } else {
        delete params.filters.publishedAt
      }
      return
    }

    if (key === "locale") {
      params.locale = value
      return
    }

    if (!excludedKeys.includes(key)) {
      if (!value) return

      if (key === "categories") {
        params.filters.categories = {
          id: { $eq: value },
        }
      } else if (key === "author") {
        params.filters.author = {
          username: { $containsi: value },
        }
      } else if (key === "domain") {
        params.filters.websites = {
          domain: { $containsi: value },
        }
      } else if (key === "title") {
        ; (params.filters as any).$and = buildPostSearchFilter(value, searchField)
      } else if (key === "sort") {
        params.sort = value
      } else if (key === "status") {
        if (value === "published") {
          params.publicationState = "live"
          params.filters.$or = [
            { status: { $containsi: "published" } },
            { publishedAt: { $notNull: true } },
          ]
        } else if (value === "draft") {
          params.publicationState = "preview"
          params.filters.$or = [
            { status: { $containsi: "draft" } },
            { publishedAt: { $null: true } },
          ]
        } else if (value !== "all") {
          params.filters.status = { $containsi: value }
        }
      } else {
        params.filters[key] = { $containsi: value }
      }
      return
    }

    if (value) {
      params.pagination = {
        ...params.pagination,
        [key]: value,
      }
    }
  })

  if (queryConfig.fields?.length) {
    ;(params as any).fields = queryConfig.fields
  }

  if (queryConfig.populate !== undefined) {
    ;(params as any).populate = queryConfig.populate
  } else {
    ;(params as any).populate = "*"
  }

  try {
    const response = await HttpClient.get(`${API_PREFIX_POST_PATH}/mine`, { params, signal })

    const modifiedResponse = response.data.map((post: any) => ({
      ...post,
      status: post.attributes.publishedAt ? "published" : "draft",
      reviewStatus: post.attributes?.reviewStatus,
      publishedAt: post.attributes.publishedAt,
    }))

    return {
      data: modifiedResponse,
      meta: response.meta,
    }
  } catch (error: any) {
    const isAbortError = signal?.aborted || error?.name === "AbortError"
    if (isAbortError) return
    message.error("Không thể lấy bài viết của bạn. Vui lòng thử lại.")
  }
}

/**
 * GET /posts/{id}/mine - Get my post by id
 */
export const fetchPostMineById = async (id: number, locale?: string) => {
  const params: Record<string, any> = {
    populate: "*",
  }

  if (locale) {
    params.locale = locale
  }

  const response = await HttpClient.get(`${API_PREFIX_POST_PATH}/${id}/mine`, { params })

  const postData = response.data

  // Đảm bảo các field mặc định giống fetchPostId
  if (postData?.attributes) {
    if (postData.attributes.isLiveNews === undefined) postData.attributes.isLiveNews = false
    if (postData.attributes.liveNewsItem === undefined) postData.attributes.liveNewsItem = []
    if (postData.attributes.views === undefined) postData.attributes.views = 0
  }

  return postData
}

/**
 * PUT /posts/{id}/mine - Update my post
 */
export const updateMine = async (id: number, data: any) => {
  return await HttpClient.put(`${API_PREFIX_POST_PATH}/${id}/mine`, data)
}

/**
 * DELETE /posts/{id}/mine - Delete my post
 */
export const deleteMine = async (id: number) => {
  return await HttpClient.delete(`${API_PREFIX_POST_PATH}/${id}/mine`)
}